import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Animated,
  Easing,
  Platform,
  StyleSheet,
  View,
  useWindowDimensions,
} from "react-native";
import { CommonActions } from "@react-navigation/native";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import { useAuth } from "@/components/AuthContext";
import {
  EVENT_FULL_FRAME_ROUTES,
  SHEET_ROUTES,
  isSingleTest,
  sectionOf,
  slugOf,
} from "@/components/navigation/routeGroups";
import Backdrop from "./Backdrop";
import EventSidebar from "./EventSidebar";
import { FrameContext } from "./FrameContext";
import { getModalFrame, getSidebarWidth } from "./layoutMode";

const MODAL_SIZES = {
  event: { maxWidth: 1120 },
  sheet: { maxWidth: 880 },
  // The single test's How to Go Live: one column, and smaller than the event's
  // frame, so the event it was started from shows around it.
  test: { maxWidth: 720, maxHeight: 800 },
};
const NO_INSETS = { top: 0, right: 0, bottom: 0, left: 0 };

const isFullFrame = (routeName) =>
  EVENT_FULL_FRAME_ROUTES.has(routeName) || SHEET_ROUTES.has(routeName);

// Where `slug`'s EventEdit sits in the stack (the screen its menu opens over).
const findEventEdit = (stackRoutes, fromIndex, slug) => {
  for (let i = stackRoutes.length - 1; i >= fromIndex; i--) {
    const r = stackRoutes[i];
    if (r.name === "EventEdit" && slugOf(r) === slug) return i;
  }
  return -1;
};

// One screen of a group. Only the top one shows; the ones under it stay mounted
// so going back keeps their state, like stack cards.
const GroupScreen = ({
  descriptor,
  visible,
  kind,
  layoutMode,
  width,
  height,
  showBack,
  switchRoom,
  insets,
}) => {
  const frame = useMemo(
    () => ({ kind, mode: layoutMode, width, height, showBack, switchRoom }),
    [kind, layoutMode, width, height, showBack, switchRoom]
  );
  return (
    <View
      style={[
        StyleSheet.absoluteFill,
        {
          paddingTop: insets.top,
          paddingRight: insets.right,
          paddingBottom: insets.bottom,
          paddingLeft: insets.left,
        },
        !visible && styles.hidden,
      ]}
    >
      <FrameContext.Provider value={frame}>
        {descriptor.render()}
      </FrameContext.Provider>
    </View>
  );
};

const EventWorkspace = ({
  routes,
  descriptors,
  navigation,
  layoutMode,
  width,
  height,
  insets,
  onClose,
}) => {
  const { prefer_multiple_tests } = useAuth();
  const firstKey = routes[0].key;
  const top = routes[routes.length - 1];
  const slug = slugOf(top);
  const sidebarWidth = getSidebarWidth(width);

  // The account's single test reached without its event in the route (a
  // reload, the review prompt's close button) gets its own frame from
  // getRouteGroup only when the event is known up front. Here it's found once
  // the menu has loaded it, and its screen then fills the frame, with no menu.
  const [testSlug, setTestSlug] = useState(null);
  const onEventLoaded = useCallback(
    (event) => {
      if (isSingleTest(event, { multipleTests: !!prefer_multiple_tests })) {
        setTestSlug(event.slug);
      }
    },
    [prefer_multiple_tests]
  );
  const singleTest = !!slug && slug === testSlug;
  const fullFrame = !slug || isFullFrame(top.name) || singleTest;

  // The menu stays mounted while switching between the rooms of the event, so
  // it doesn't blink and the room chips keep their scroll position. Any other
  // event gets a new one.
  const [menu, setMenu] = useState({ key: slug, slugs: [slug] });
  if (!menu.slugs.includes(slug)) {
    setMenu({ key: slug, slugs: [slug] });
  }

  // Menu: show the screen right over the event's EventEdit (Overview: EventEdit
  // itself), so back goes to the overview and the history stays one level deep.
  const openSection = useCallback(
    (screen, event) => {
      const state = navigation.getState();
      const start = state.routes.findIndex((r) => r.key === firstKey);
      if (start < 0) return;
      const base = findEventEdit(state.routes, start, slug);
      const kept =
        base >= 0
          ? state.routes.slice(0, base + 1)
          : [
              ...state.routes.slice(0, start),
              { name: "EventEdit", params: { slug, event } },
            ];
      const open = base >= 0 ? state.routes[base + 1] : null;
      const next =
        screen === "EventEdit"
          ? kept
          : [
              ...kept,
              open?.name === screen && slugOf(open) === slug
                ? open
                : { name: screen, params: { event, slug } },
            ];
      if (
        next.length === state.routes.length &&
        next.every((r, i) => r.key === state.routes[i].key)
      ) {
        return;
      }
      navigation.dispatch(
        CommonActions.reset({ ...state, routes: next, index: next.length - 1 })
      );
    },
    [navigation, firstKey, slug]
  );

  // Room chips (the menu's, and How to Go Live's in the pane): the other room,
  // on the same menu item. The room's copy from the chips goes with the route,
  // so its screens show it straight away instead of loading it first.
  const switchRoomRef = useRef(null);
  // `section` picks the room's screen instead: a room just added opens on its
  // overview ("EventEdit"), not on Add a room again.
  switchRoomRef.current = (member, { section: toSection } = {}) => {
    const state = navigation.getState();
    const start = state.routes.findIndex((r) => r.key === firstKey);
    if (start < 0) return;
    const base = findEventEdit(state.routes, start, slug);
    const section = toSection || sectionOf(top.name);
    const params = { slug: member.slug, event: member };
    const next = [
      ...state.routes.slice(0, base >= 0 ? base : start),
      { name: "EventEdit", params },
      ...(section !== "EventEdit" && !isFullFrame(section)
        ? [{ name: section, params }]
        : []),
    ];
    setMenu((m) =>
      m.slugs.includes(member.slug)
        ? m
        : { ...m, slugs: [...m.slugs, member.slug] }
    );
    navigation.dispatch(
      CommonActions.reset({ ...state, routes: next, index: next.length - 1 })
    );
  };
  const switchRoom = useCallback(
    (member, options) => switchRoomRef.current(member, options),
    []
  );

  const paneWidth =
    width - insets.right - (fullFrame ? insets.left : sidebarWidth);
  const paneHeight = height - insets.top - insets.bottom;

  return (
    <View style={styles.row}>
      <View
        style={[
          styles.sidebar,
          {
            width: sidebarWidth,
            paddingTop: insets.top,
            paddingLeft: insets.left,
            paddingBottom: insets.bottom,
          },
          fullFrame && styles.hidden,
        ]}
      >
        {!!slug && !singleTest && (
          <EventSidebar
            key={menu.key}
            slug={slug}
            initialEvent={top.params?.event}
            activeSection={sectionOf(top.name)}
            navigation={navigation}
            onOpen={openSection}
            onSwitchRoom={switchRoom}
            onEventLoaded={onEventLoaded}
            onClose={onClose}
          />
        )}
      </View>
      <View style={styles.flex}>
        {routes.map((route, index) => {
          const kind = isFullFrame(route.name) ? "full" : "pane";
          const routeSlug = slugOf(route);
          // Checkout screens, and the single test's screens (no menu for it).
          const fills = kind === "full" || (singleTest && routeSlug === slug);
          const base = findEventEdit(routes.slice(0, index + 1), 0, routeSlug);
          const depth = base >= 0 ? index - base : index + 1;
          return (
            <GroupScreen
              key={route.key}
              descriptor={descriptors[route.key]}
              visible={index === routes.length - 1}
              kind={kind}
              layoutMode={layoutMode}
              width={fills ? width - insets.left - insets.right : paneWidth}
              height={paneHeight}
              // A menu item's own screen needs no back button, the menu is beside it.
              showBack={
                fills || depth > 1 || sectionOf(route.name) !== route.name
              }
              switchRoom={kind === "pane" ? switchRoom : undefined}
              insets={{
                ...insets,
                left: fills ? insets.left : 0,
              }}
            />
          );
        })}
      </View>
    </View>
  );
};

const SheetStack = ({
  routes,
  descriptors,
  layoutMode,
  width,
  height,
  insets,
}) => (
  <View style={styles.flex}>
    {routes.map((route, index) => (
      <GroupScreen
        key={route.key}
        descriptor={descriptors[route.key]}
        visible={index === routes.length - 1}
        kind="sheet"
        layoutMode={layoutMode}
        width={width - insets.left - insets.right}
        height={height - insets.top - insets.bottom}
        showBack
        insets={insets}
      />
    ))}
  </View>
);

// A run of grouped screens as one stack card (components/navigation/AdaptiveStack.js).
// Split layout: full screen. Modal layout: a centred frame over the blurred
// screen under it (the dashboard, or the event a test was started from), or
// over a plain backdrop when it was opened straight from a link with nothing
// under it. Events get the menu beside the open screen; account screens and
// the single test's How to Go Live show one screen at a time.
const AdaptiveGroup = ({
  type,
  layoutMode,
  routes,
  descriptors,
  navigation,
  hasBackground,
}) => {
  const window = useWindowDimensions();
  const deviceInsets = useSafeAreaInsets();
  const modal = layoutMode === "modal";
  const size = MODAL_SIZES[type] || MODAL_SIZES.sheet;
  const box = modal
    ? getModalFrame(window, deviceInsets, size.maxWidth, size.maxHeight)
    : null;
  const firstKey = routes[0].key;

  // The modal fades in when it opens (not when a rotation turns a split view
  // into a modal). Only the frame and the backdrop's own layers fade: a
  // see-through parent would stop the backdrop blurring what's behind it.
  const appear = useRef(new Animated.Value(modal ? 0 : 1)).current;
  useEffect(() => {
    Animated.timing(appear, {
      toValue: 1,
      duration: 200,
      easing: Easing.out(Easing.quad),
      useNativeDriver: Platform.OS !== "web",
    }).start();
  }, [appear]);

  // Close the whole group: back to whatever it was opened over.
  const close = useCallback(() => {
    const state = navigation.getState();
    const start = state.routes.findIndex((r) => r.key === firstKey);
    const next =
      start > 0 ? state.routes.slice(0, start) : [{ name: "EventList" }];
    navigation.dispatch(
      CommonActions.reset({ ...state, routes: next, index: next.length - 1 })
    );
  }, [navigation, firstKey]);

  const Content = type === "event" ? EventWorkspace : SheetStack;
  const content = (
    <Content
      routes={routes}
      descriptors={descriptors}
      navigation={navigation}
      layoutMode={layoutMode}
      width={modal ? box.width : window.width}
      height={modal ? box.height : window.height}
      insets={modal ? NO_INSETS : deviceInsets}
      onClose={close}
    />
  );

  if (!modal) {
    return (
      <View style={[StyleSheet.absoluteFill, styles.screen]}>{content}</View>
    );
  }

  return (
    <View style={StyleSheet.absoluteFill}>
      <Backdrop onPress={close} blur={hasBackground} appear={appear} />
      <Animated.View style={[styles.modalShadow, box, { opacity: appear }]}>
        <View style={styles.modal}>{content}</View>
      </Animated.View>
    </View>
  );
};

const styles = StyleSheet.create({
  flex: { flex: 1 },
  row: { flex: 1, flexDirection: "row" },
  hidden: { display: "none" },
  screen: { backgroundColor: "white" },
  sidebar: {
    backgroundColor: "#F8FAFC",
    borderRightWidth: StyleSheet.hairlineWidth,
    borderRightColor: "#D1D5DB",
  },
  modalShadow: {
    position: "absolute",
    borderRadius: 16,
    backgroundColor: "white",
    shadowColor: "#0F172A",
    shadowOpacity: 0.22,
    shadowRadius: 32,
    shadowOffset: { width: 0, height: 12 },
    elevation: 24,
  },
  modal: { flex: 1, borderRadius: 16, overflow: "hidden" },
});

export default AdaptiveGroup;
